/**
 * The budget-gated pilot allocations — HASE, skill triggers, talents, license ranks, and core
 * bonuses. Every one of these reads a `base + level` budget out of `../budgets`/`../licenses` and
 * rejects an over-spend rather than clamping it.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type ApplyCtx, type Result } from "@lancer-vtt/shared";
import { MissingContentError } from "@lancer-vtt/compendium";
import { HASE_BUDGET_BASE, MAX_TRIGGER_RANK, SKILL_BUDGET_BASE, TALENT_BUDGET_BASE, coreBonusBudget, rankCeilingAtLevel, rankSum } from "../budgets";
import type { SetCoreBonusCommand, SetLicenseRankCommand, SetMechSkillCommand, SetSkillTriggerCommand, SetTalentRankCommand } from "../commands";
import { MAX_LICENSE_RANK, licenseBudget, meetsCoreBonusPrereq, spentLicenseRanks, withCoreBonus, withLicenseRank } from "../licenses";
import type { MechSkills, PilotData, RosterState } from "../types";
import { MAX_SKILL, MIN_SKILL, changed, requireCompendium, requirePilot, withPilot, type RosterApplyResult } from "./context";

// ── set_mech_skill (HASE budget) ───────────────────────────────────────────────

export function haseSum(skills: MechSkills): number {
  return skills.hull + skills.agi + skills.sys + skills.eng;
}

export function applySetMechSkill(
  state: RosterState,
  cmd: SetMechSkillCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (!Number.isInteger(cmd.value) || cmd.value < MIN_SKILL || cmd.value > MAX_SKILL) {
    return err(
      new CommandError(
        `set_mech_skill: ${cmd.skill}=${cmd.value} out of range ${MIN_SKILL}–${MAX_SKILL}`,
      ),
    );
  }
  const prev = pilot.mechSkills[cmd.skill];
  const nextSkills: MechSkills = { ...pilot.mechSkills, [cmd.skill]: cmd.value };
  const budget = HASE_BUDGET_BASE + pilot.level;
  const used = haseSum(nextSkills);
  if (used > budget) {
    return err(
      new CommandError(
        `set_mech_skill: total HASE ${used} would exceed budget ${budget} (${HASE_BUDGET_BASE} + level ${pilot.level})`,
      ),
    );
  }
  const next: PilotData = { ...pilot, mechSkills: nextSkills };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "set_mech_skill",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      skill: cmd.skill,
      value: prev,
    },
    events: changed(cmd.pilotId),
  });
}

// ── set_skill_trigger / set_talent_rank (budget-gated allocation maps) ──────────

/**
 * Set one key in a `Record<id, rank>` allocation map to `rank`, or delete it when
 * `rank === 0`. Returns a new map (immutable) — the swap that backs both the
 * forward command and its symmetric inverse.
 */
export function withRank(
  map: Record<string, number>,
  id: string,
  rank: number,
): Record<string, number> {
  if (rank === 0) {
    const { [id]: _removed, ...rest } = map;
    return rest;
  }
  return { ...map, [id]: rank };
}

/**
 * Set a pilot skill trigger to an absolute rank (0–3). Mirrors `applySetMechSkill`:
 * range-check (plus the LL0 rank-1 shape ceiling), then a `4 + level` budget gate over
 * the *resulting* total, hard-reject over budget. The inverse is a symmetric `set_skill_trigger` to the prior rank (0 if
 * the trigger wasn't owned) — a `Record` value swap, no lossy capture (the gotchas).
 * Raw-only: a skill carries no compendium rule, so no `ctx` is consulted.
 */
export function applySetSkillTrigger(
  state: RosterState,
  cmd: SetSkillTriggerCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (!Number.isInteger(cmd.rank) || cmd.rank < 0 || cmd.rank > MAX_TRIGGER_RANK) {
    return err(
      new CommandError(
        `set_skill_trigger: rank ${cmd.rank} out of range 0–${MAX_TRIGGER_RANK}`,
      ),
    );
  }
  // The LL0 shape wall: four *distinct* +2 triggers, so no key may exceed rank I until
  // reallocation exists at LL1+ (see `rankCeilingAtLevel`).
  const rankCeiling = rankCeilingAtLevel(pilot.level, MAX_TRIGGER_RANK);
  if (cmd.rank > rankCeiling) {
    return err(
      new CommandError(
        `set_skill_trigger: rank ${cmd.rank} exceeds the LL0 ceiling of ${rankCeiling}; an LL0 pilot takes four distinct rank-I triggers`,
      ),
    );
  }
  const prev = pilot.skills[cmd.skillId] ?? 0;
  const nextSkills = withRank(pilot.skills, cmd.skillId, cmd.rank);
  const budget = SKILL_BUDGET_BASE + pilot.level;
  const used = rankSum(nextSkills);
  if (used > budget) {
    return err(
      new CommandError(
        `set_skill_trigger: total skill ranks ${used} would exceed budget ${budget} (${SKILL_BUDGET_BASE} + level ${pilot.level})`,
      ),
    );
  }
  const next: PilotData = { ...pilot, skills: nextSkills };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "set_skill_trigger",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      skillId: cmd.skillId,
      rank: prev,
    },
    events: changed(cmd.pilotId),
  });
}

/**
 * Set a pilot talent to an absolute rank (0..ranks.length). Reference-dependent:
 * resolves the talent against `ctx.compendium` to range-check `rank ≤ ranks.length`
 * (a removal, `rank 0`, needs no resolution but we resolve uniformly for a clear
 * unknown-id error) and, at LL0, the rank-1 shape ceiling. Then the `3 + level` budget
 * gate over the resulting total, hard-reject over budget. Symmetric `set_talent_rank`
 * inverse, no lossy capture.
 */
export function applySetTalentRank(
  state: RosterState,
  cmd: SetTalentRankCommand,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (!Number.isInteger(cmd.rank) || cmd.rank < 0) {
    return err(
      new CommandError(`set_talent_rank: rank ${cmd.rank} must be a non-negative integer`),
    );
  }
  // A non-zero rank must resolve the talent and fit its rank ceiling. A removal
  // (rank 0) is allowed even for an id the pilot somehow owns but the compendium
  // lacks — it only shrinks the allocation — but we still resolve for symmetry and
  // surface an unknown id via MissingContentError when the pilot is *adding*.
  if (cmd.rank > 0) {
    const compendiumR = requireCompendium(ctx);
    if (!compendiumR.ok) return compendiumR;
    const compendium = compendiumR.value;
    const talent = compendium.getById("talent", cmd.talentId);
    if (!talent) {
      throw new MissingContentError("talent", cmd.talentId, `pilot ${cmd.pilotId}`);
    }
    const maxRank = (talent as { ranks?: unknown[] }).ranks?.length ?? 0;
    if (cmd.rank > maxRank) {
      return err(
        new CommandError(
          `set_talent_rank: rank ${cmd.rank} exceeds talent ${cmd.talentId}'s ${maxRank} rank(s)`,
        ),
      );
    }
    // The LL0 shape wall: three *distinct* rank-I talents, so no key may exceed rank I
    // until reallocation exists at LL1+ (see `rankCeilingAtLevel`).
    const rankCeiling = rankCeilingAtLevel(pilot.level, maxRank);
    if (cmd.rank > rankCeiling) {
      return err(
        new CommandError(
          `set_talent_rank: rank ${cmd.rank} exceeds the LL0 ceiling of ${rankCeiling}; an LL0 pilot takes three distinct rank-I talents`,
        ),
      );
    }
  }
  const prev = pilot.talents[cmd.talentId] ?? 0;
  const nextTalents = withRank(pilot.talents, cmd.talentId, cmd.rank);
  const budget = TALENT_BUDGET_BASE + pilot.level;
  const used = rankSum(nextTalents);
  if (used > budget) {
    return err(
      new CommandError(
        `set_talent_rank: total talent ranks ${used} would exceed budget ${budget} (${TALENT_BUDGET_BASE} + level ${pilot.level})`,
      ),
    );
  }
  const next: PilotData = { ...pilot, talents: nextTalents };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "set_talent_rank",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      talentId: cmd.talentId,
      rank: prev,
    },
    events: changed(cmd.pilotId),
  });
}

/**
 * Set a pilot license to an absolute rank (0–3). Raw-only — a license id carries no
 * compendium rule beyond existing (the equip gate that reads `license_level` lives in
 * `licenses.ts`), so no `ctx` is consulted. Range-check `0 ≤ rank ≤ 3`, then the per-LL budget
 * gate over the *resulting* spent total (`Σ non-GMS ranks ≤ level`; GMS is the free,
 * seeded license and is excluded). Hard-reject over budget. The inverse is a symmetric
 * `set_license_rank` to the prior rank (0 if not owned) — an array value swap, no lossy
 * capture (the `set_skill_trigger`/`set_talent_rank` model). The single source for the
 * cap is `licenses.ts`, which the kit affordance mirrors.
 */
export function applySetLicenseRank(
  state: RosterState,
  cmd: SetLicenseRankCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (!Number.isInteger(cmd.rank) || cmd.rank < 0 || cmd.rank > MAX_LICENSE_RANK) {
    return err(
      new CommandError(
        `set_license_rank: rank ${cmd.rank} out of range 0–${MAX_LICENSE_RANK}`,
      ),
    );
  }
  const prev = pilot.licenses.find((l) => l.id === cmd.licenseId)?.rank ?? 0;
  const nextLicenses = withLicenseRank(pilot.licenses, cmd.licenseId, cmd.rank);
  const next: PilotData = { ...pilot, licenses: nextLicenses };
  // Per-LL budget over the *resulting* spend. GMS is free (excluded by
  // `spentLicenseRanks`), so taking/raising GMS never overflows — only non-GMS ranks
  // count, and their sum must fit `level` (1 rank/level, Core p. 18).
  const budget = licenseBudget(pilot);
  const used = spentLicenseRanks(next);
  if (used > budget) {
    return err(
      new CommandError(
        `set_license_rank: total license ranks ${used} would exceed budget ${budget} (1 per level, level ${pilot.level})`,
      ),
    );
  }
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "set_license_rank",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      licenseId: cmd.licenseId,
      rank: prev,
    },
    events: changed(cmd.pilotId),
  });
}

/**
 * Take or clear a pilot core bonus (`taken` toggle). A **clear** is raw-only — removing
 * an id only shrinks the set, so no compendium and no gates. A **take** is reference-
 * dependent: resolve the bonus against `ctx.compendium` for its `source` (manufacturer),
 * then gate twice — the per-LL **budget** (`coreBonusBudget = floor(level/3)`) over the
 * resulting count, and the per-manufacturer **prerequisite** (`meetsCoreBonusPrereq`; GMS
 * always passes). Both must pass; hard-reject otherwise (the `set_talent_rank` model).
 * Inverse is a symmetric `set_core_bonus` back to the prior held-state — an id-list swap,
 * no lossy capture. Idempotent re-take / re-clear round-trips (inverse restores `prev`).
 */
export function applySetCoreBonus(
  state: RosterState,
  cmd: SetCoreBonusCommand,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const prevHeld = pilot.coreBonuses.includes(cmd.bonusId);

  // A take resolves the bonus and runs both gates; a clear never needs the compendium
  // and never exceeds a budget (it only shrinks the set), mirroring `set_talent_rank`'s
  // rank-0 removal path.
  if (cmd.taken && !prevHeld) {
    const compendiumR = requireCompendium(ctx);
    if (!compendiumR.ok) return compendiumR;
    const compendium = compendiumR.value;
    const bonus = compendium.getById("core_bonus", cmd.bonusId);
    if (!bonus) {
      throw new MissingContentError("core_bonus", cmd.bonusId, `pilot ${cmd.pilotId}`);
    }
    // Budget: total taken bonuses after this take must fit floor(level/3).
    const budget = coreBonusBudget(pilot);
    const used = pilot.coreBonuses.length + 1;
    if (used > budget) {
      return err(
        new CommandError(
          `set_core_bonus: taking ${cmd.bonusId} would make ${used} core bonus(es), over budget ${budget} (1 per 3rd LL, level ${pilot.level})`,
        ),
      );
    }
    // Prerequisite: per-manufacturer license-rank gate (GMS exempt). `alreadyHeld` is
    // how many bonuses from the SAME manufacturer the pilot already holds (the rule is
    // cumulative — the Nth needs 3N ranks). Resolve each held id's source off the
    // compendium (manufacturer is derived, never stored).
    let alreadyHeld = 0;
    for (const heldId of pilot.coreBonuses) {
      const held = compendium.getById("core_bonus", heldId);
      if (held && held.source === bonus.source) alreadyHeld += 1;
    }
    if (!meetsCoreBonusPrereq({ pilot, bonus, compendium, alreadyHeld })) {
      return err(
        new CommandError(
          `set_core_bonus: ${cmd.bonusId} requires ${(alreadyHeld + 1) * 3} ${bonus.source} license rank(s) (held ${alreadyHeld} ${bonus.source} bonus(es) already)`,
        ),
      );
    }
  }

  const nextBonuses = withCoreBonus(pilot.coreBonuses, cmd.bonusId, cmd.taken);
  const next: PilotData = { ...pilot, coreBonuses: nextBonuses };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "set_core_bonus",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      bonusId: cmd.bonusId,
      taken: prevHeld,
    },
    events: changed(cmd.pilotId),
  });
}
